/**
 * Page de liste des abonnements (admin)
 */

import { useState } from 'react';
import { useAdminSubscriptions } from '@/hooks/admin/useAdminSubscriptions';
import { AdminKPICard } from '@/components/admin/AdminKPICard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreditCard,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function AdminSubscriptions() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const { subscriptions, stats, totalCount, totalPages, loading, error } =
    useAdminSubscriptions({
      page,
      pageSize: 20,
      statusFilter,
    });

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'entreprise':
        return <Badge className="bg-purple-500">Entreprise</Badge>;
      case 'pro':
        return <Badge className="bg-blue-500">Pro</Badge>;
      case 'artisan':
        return <Badge className="bg-green-500">Artisan</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Actif
          </Badge>
        );
      case 'trialing':
        return (
          <Badge className="bg-amber-500 gap-1">
            <Clock className="h-3 w-3" />
            Essai
          </Badge>
        );
      case 'past_due':
        return (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            Impaye
          </Badge>
        );
      case 'canceled':
        return (
          <Badge variant="secondary" className="gap-1">
            <XCircle className="h-3 w-3" />
            Annule
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlanPrice = (plan: string, billingPeriod: string | null) => {
    const prices: Record<string, { monthly: number; yearly: number }> = {
      free: { monthly: 0, yearly: 0 },
      artisan: { monthly: 29, yearly: 290 },
      pro: { monthly: 79, yearly: 790 },
      entreprise: { monthly: 199, yearly: 1990 },
    };
    const p = prices[plan] || { monthly: 0, yearly: 0 };
    if (billingPeriod === 'yearly') {
      return `${p.yearly} € /an`;
    }
    return `${p.monthly} € /mois`;
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Erreur : {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Abonnements</h1>
          <p className="text-muted-foreground">
            {totalCount} abonnement{totalCount > 1 ? 's' : ''}
          </p>
        </div>
        <CreditCard className="h-10 w-10 text-muted-foreground" />
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <AdminKPICard
            title="MRR Total"
            value={`${stats.totalMRR.toLocaleString('fr-FR')} €`}
            icon={DollarSign}
            variant="primary"
          />
          <AdminKPICard
            title="Actifs"
            value={stats.activeCount}
            icon={CheckCircle}
            variant="success"
          />
          <AdminKPICard
            title="En essai"
            value={stats.trialingCount}
            icon={Clock}
            variant="warning"
          />
          <AdminKPICard
            title="Impayes"
            value={stats.pastDueCount}
            icon={AlertTriangle}
            variant="danger"
          />
          <AdminKPICard
            title="Annules"
            value={stats.canceledCount}
            icon={XCircle}
          />
        </div>
      )}

      {/* Filtre */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <span className="text-sm font-medium">Filtrer par statut :</span>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="trialing">En essai</SelectItem>
                <SelectItem value="past_due">Impayes</SelectItem>
                <SelectItem value="canceled">Annules</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des abonnements</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Prochaine facture</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">
                        {sub.entreprise_nom}
                      </TableCell>
                      <TableCell>{getPlanBadge(sub.plan)}</TableCell>
                      <TableCell>
                        {getPlanPrice(sub.plan, sub.billing_period)}
                      </TableCell>
                      <TableCell>{getStatusBadge(sub.status)}</TableCell>
                      <TableCell>
                        {sub.current_period_end ? (
                          <div>
                            <p>
                              {format(
                                new Date(sub.current_period_end),
                                'dd MMM yyyy',
                                { locale: fr }
                              )}
                            </p>
                            {sub.cancel_at_period_end && (
                              <p className="text-xs text-destructive">
                                Annulation prevue
                              </p>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {sub.stripe_customer_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              window.open(
                                `https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`,
                                '_blank'
                              )
                            }
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Stripe
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {subscriptions.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground py-8"
                      >
                        Aucun abonnement trouve
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} sur {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Precedent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
